/**
 * Multi-Select Field Type
 *
 * Provides configuration and renderers for multi-select fields:
 * - Read-only column renderer (displays values as bordered Tags)
 * - Editable column renderer (multi-select ComboBox)
 * - Form field renderer (multi-select ComboBox for modals)
 *
 * Used for custom fields with "Multiple allowed values = yes"
 * Uses the same Tag styled component as the 'tags' field type for visual consistency.
 */

import { memo } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { CaretDown } from '@phosphor-icons/react';
import { t } from '@lingui/macro';
import { Box } from '@mui/material';
import { Text } from 'components/text';
import { TagsWithOverflow } from '../../components/TagsWithOverflow';
import type { ComboBoxApi, ComboBoxOption } from 'components/inputs/types';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
  MultiSelectValue,
} from '../../types';
import { validateFieldValue } from '../../validation';
import { FieldError, shouldShowFieldError } from './fieldError';
import { ClearedFieldIndicator } from '../../components/ClearedFieldIndicator';
import { BULK_EDIT_CLEAR } from '../../constants';
import { L } from './columnWidths';
import { CellContainer } from '../../styles';
import { EditableMultiselectCell, TableAutocomplete } from '../../components';
import { renderMultiselectTags } from '../../components/multiselectInlineEditUtils';

// Import shared cell value utilities (single source of truth for parsing)
import { getCellValue, parseMultiSelectValue } from './cellValue';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get multi-select values from cell context
 * Uses shared getCellValue (respects accessorFn) and shared parseMultiSelectValue
 */
const getCellMultiSelectValues = <T extends { id: number | string }>(
  context: CellContext<T, unknown>
): MultiSelectValue[] => {
  const value = getCellValue(context, context.column.id);
  return parseMultiSelectValue(value);
};

/**
 * Convert MultiSelectValue array to display string for tooltip
 */
const getDisplayString = (values: MultiSelectValue[]): string => {
  return values.map(v => v.label).join(', ');
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Memoized read-only multiselect cell component
 */
const MultiselectReadOnlyCell = memo(function MultiselectReadOnlyCell({
  values,
  columnWidth,
}: {
  values: MultiSelectValue[];
  columnWidth?: number;
}) {
  if (values.length === 0) {
    return <CellContainer> </CellContainer>;
  }

  return (
    <CellContainer>
      <TagsWithOverflow values={values} columnWidth={columnWidth} />
    </CellContainer>
  );
});

/**
 * Multi-select read-only renderer - displays values as bordered Tags
 * Uses getValue() to support nested accessors
 * Includes internal Tooltip for consistent hover behavior
 */
export const multiselectReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    // Use getCellMultiSelectValues to respect accessorFn (important for custom fields)
    const values = getCellMultiSelectValues(cellContext);

    return <MultiselectReadOnlyCell values={values} columnWidth={cellContext.column.getSize()} />;
  },
});

// ============================================================================
// Form Field Renderer
// ============================================================================

// Import getLookupApi lazily to avoid circular dependencies
let getLookupApiFn: ((columnId: string) => unknown) | null = null;

/**
 * Set the getLookupApi function (called from index.ts to avoid circular deps)
 */
export const setGetLookupApiForMultiselect = (fn: (columnId: string) => unknown): void => {
  getLookupApiFn = fn;
};

/**
 * Render a multi-select field for forms
 */
export const renderMultiselectField = ({
  col,
  value,
  valueForValidation,
  onChange,
  disabled,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;

  // Get API config: explicit > registry > static options
  const apiConfig =
    col.api || (getLookupApiFn ? (getLookupApiFn(col.key) as typeof col.api) : null);

  const allowEmptySearchFetch = Boolean(bulkEditContext?.isBulkEdit && apiConfig?.search);

  // Convert current value to array of ComboBoxOption
  const currentValues: ComboBoxOption[] = Array.isArray(value)
    ? value.map(v => {
        if (typeof v === 'object' && v !== null) {
          const obj = v as Record<string, unknown>;
          return {
            id: (obj.id as string | number) ?? '',
            label: (obj.label as string) ?? (obj.name as string) ?? String(obj.id ?? ''),
          };
        }
        return { id: String(v), label: String(v) };
      })
    : [];

  const valueEmpty = currentValues.length === 0;
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  // Use Zod validation to get error message (valueForValidation for bulk-edit "keep current")
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : currentValues;
  const validation = validateFieldValue({ ...col, type: 'multiselect' }, valueToValidate);
  const errorMessage = !validation.success ? validation.error : undefined;
  const showError = shouldShowFieldError(col.key, validationContext);

  const handleChange = (newValue: ComboBoxOption[] | null) => {
    onChange(col.key, newValue);
    validationContext?.onFieldTouched?.(col.key);
  };

  const isBulkEdit = bulkEditContext?.isBulkEdit ?? false;
  const isCleared = valueForValidation === BULK_EDIT_CLEAR;
  const isRequired = (col as { required?: boolean }).required ?? false;
  const hasExistingValue = bulkEditContext?.hasExistingValue ?? false;
  const hideSetEmpty = bulkEditContext?.hideSetEmpty ?? true;
  // Keep "Set empty" pinned in the menu as long as items have a pre-existing
  // value to clear — regardless of what the user has picked so far. Matches
  // the single-dropdown behaviour in `getBulkEditClearState.showClearOption`.
  const showSetEmptyInDropdown = isBulkEdit && !hideSetEmpty && !isRequired && hasExistingValue;

  const handleSetEmpty = () => {
    onChange(col.key, BULK_EDIT_CLEAR);
  };

  const handleUndoClear = () => {
    bulkEditContext?.onKeepCurrent();
  };

  if (isBulkEdit && isCleared) {
    return (
      <Box key={col.key}>
        <ClearedFieldIndicator fieldName={col.label} onClick={handleUndoClear} />
      </Box>
    );
  }

  return (
    <Box key={col.key}>
      <TableAutocomplete
        id={col.key}
        label={col.label}
        placeholder={showEmptyChip ? '' : col.placeholder}
        api={apiConfig}
        allowEmptySearchFetch={allowEmptySearchFetch}
        options={col.options}
        variant="outlined"
        labelSeparate
        multiple
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        value={currentValues}
        disabled={disabled}
        // Match inline edit behavior
        renderTags={renderMultiselectTags}
        disableCloseOnSelect
        sortSelectedFirst
        selectedValues={currentValues}
        clearOption={
          showSetEmptyInDropdown ? { id: '__set_empty__', label: t`Set empty` } : undefined
        }
        showClearOption={showSetEmptyInDropdown}
        onSetEmpty={handleSetEmpty}
        onChange={(_, newValue, reason) => {
          if (reason === 'clear' && bulkEditContext?.isBulkEdit) {
            bulkEditContext.onKeepCurrent();
            return;
          }
          // Handle set empty option
          const hasSetEmpty = newValue?.some(v => String(v?.id) === '__set_empty__');
          if (hasSetEmpty) {
            handleSetEmpty();
            return;
          }
          // Handle clear option
          const hasClear = newValue?.some(v => String(v?.id) === '__clear__');
          if (hasClear) {
            handleChange([]);
            return;
          }
          handleChange(newValue);
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (
            option.id === '__clear__' ||
            val.id === '__clear__' ||
            option.id === '__set_empty__' ||
            val.id === '__set_empty__'
          )
            return false;
          return String(option.id) === String(val.id);
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          return option?.label ?? '';
        }}
        InputProps={showEmptyChip ? { startAdornment: emptyDisplayNode } : undefined}
      />
      <FieldError error={errorMessage} show={showError} />
    </Box>
  );
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Multi-select editable renderer - supports API-backed (registry) and static options
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 *
 * When API is resolved (e.g. categories from registry): uses TableAutocomplete with api + multiple.
 * When only static col.options: uses TableAutocomplete with static options.
 * When neither: renders read-only and meta.editable false.
 */
export const multiselectEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  // Resolve API: explicit col.api > registry by column id (same as dropdown and form multiselect)
  const resolvedApi = (col.api ||
    (getLookupApiFn ? (getLookupApiFn(col.id) as ComboBoxApi | null) : null)) as ComboBoxApi | null;
  const hasStaticOptions = Boolean(col.options && col.options.length > 0);
  const hasNoDataSource = !resolvedApi && !hasStaticOptions;

  const editableProp = col.editable;

  // No data source: render read-only and disable edit (same fallback as dropdown)
  if (hasNoDataSource) {
    return {
      cell: (context: CellContext<T, unknown>) => {
        const values = getCellMultiSelectValues(context);
        return (
          <CellContainer>
            {values.length === 0 ? null : (
              <TagsWithOverflow values={values} columnWidth={context.column.getSize()} />
            )}
          </CellContainer>
        );
      },
      meta: {
        editable: false,
      },
    };
  }

  return {
    cell: (context: CellContext<T, unknown>) => {
      const values = getCellMultiSelectValues(context);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      const readOnlyDisplay = (
        <CellContainer>
          {values.length === 0 ? null : (
            <TagsWithOverflow values={values} columnWidth={context.column.getSize()} />
          )}
        </CellContainer>
      );

      if (!isRowEditable) {
        return readOnlyDisplay;
      }

      const currentValues: ComboBoxOption[] = values.map(v => ({ id: v.id, label: v.label }));

      return (
        <EditableMultiselectCell<T>
          context={context}
          value={currentValues}
          onUpdate={(row, update) => col.onUpdate?.(row, update)}
          tooltipTitle={getDisplayString(values)}
          placeholder=""
          api={resolvedApi}
          options={col.options ?? []}
          columnId={col.id}
          required={'required' in col ? !!col.required : undefined}
          columnLabel={col.label}
          disabled={!isRowEditable}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Multi-select field type configuration
 */
export const multiselectFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...L,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const multiselectReadOnly: ColumnRenderer = multiselectReadOnlyRenderer;

/** Editable renderer with proper typing */
export const multiselectEditable: ColumnRenderer = multiselectEditableRenderer;
